import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, RefreshCw, Search } from "lucide-react";
import { adminListPayments } from "../../lib/api";

const STATUS_STYLES = {
  paid: "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)]/30",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-600 border-red-200",
  refunded: "bg-purple-50 text-purple-700 border-purple-200",
  disputed: "bg-orange-50 text-orange-700 border-orange-200",
  expired: "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] border-[var(--ed-rule)]",
  initiated: "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] border-[var(--ed-rule)]",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] border-[var(--ed-rule)]";
  return (
    <span className={`text-[0.55rem] uppercase tracking-[0.2em] px-2 py-1 border ${cls}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

function fmt(n) {
  return "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-[var(--ed-rule)] bg-[var(--ed-bg)] rounded-sm p-5">
      <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)] mb-2">{label}</p>
      <p className={`font-serif text-[1.7rem] leading-none ${accent || "text-[var(--ed-ink)]"}`}>{value}</p>
    </div>
  );
}

export default function PaymentsPanel() {
  const [data, setData] = useState({ rows: [], totals: { paid: 0, pending: 0, refunded: 0, cancelled: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { setData(await adminListPayments()); }
    catch { toast.error("Could not load payments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (filter !== "all") {
        if (filter === "pending" && r.status !== "pending_payment") return false;
        if (filter !== "pending" && r.status !== filter) return false;
      }
      if (needle) {
        const hay = `${r.full_name || ""} ${r.email || ""} ${r.property_name || ""} ${r.stripe_session_id || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [data.rows, filter, q]);

  const counts = useMemo(() => {
    const c = { all: data.rows.length, paid: 0, pending: 0, refunded: 0, cancelled: 0, disputed: 0 };
    for (const r of data.rows) {
      if (r.status === "paid") c.paid++;
      else if (r.status === "pending_payment") c.pending++;
      else if (r.status === "refunded") c.refunded++;
      else if (r.status === "cancelled") c.cancelled++;
      else if (r.status === "disputed") c.disputed++;
    }
    return c;
  }, [data.rows]);

  const stripeBase = "https://dashboard.stripe.com/payments";

  return (
    <div data-testid="payments-panel">
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="ed-overline mb-2">Operations</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Payments</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5">All Stripe-tracked bookings · deep-link to the Stripe Dashboard for refunds and disputes.</p>
        </div>
        <button data-testid="pay-refresh" onClick={load} className="text-[0.65rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] text-[var(--ed-ink-soft)] hover:text-[var(--ed-ink)] px-3 py-2.5 rounded-sm flex items-center gap-1.5">
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <Stat label="Paid (gross)"  value={fmt(data.totals.paid)}      accent="text-[var(--ed-emerald)]" />
        <Stat label="Pending"       value={fmt(data.totals.pending)}   accent="text-amber-600" />
        <Stat label="Refunded"      value={fmt(data.totals.refunded)}  accent="text-purple-700" />
        <Stat label="Cancelled"     value={fmt(data.totals.cancelled)} accent="text-[var(--ed-ink-muted)]" />
      </div>

      <div className="flex flex-wrap gap-3 mb-6 pb-5 border-b border-[var(--ed-rule)] items-center">
        <div className="flex flex-wrap gap-2">
          {[
            ["all", `All · ${counts.all}`],
            ["paid", `Paid · ${counts.paid}`],
            ["pending", `Pending · ${counts.pending}`],
            ["refunded", `Refunded · ${counts.refunded}`],
            ["cancelled", `Cancelled · ${counts.cancelled}`],
            ["disputed", `Disputed · ${counts.disputed}`],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`pay-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 border rounded-sm transition-colors ${
                filter === k
                  ? "bg-[var(--ed-ink)] text-[var(--ed-bg)] border-[var(--ed-ink)]"
                  : "border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)]"
              }`}
            >{l}</button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ed-ink-muted)]" />
          <input
            data-testid="pay-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search guest, property, session id"
            className="w-full pl-9 pr-3 py-2 text-sm bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] text-[var(--ed-ink)] outline-none rounded-sm"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-[var(--ed-ink-muted)] py-16 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--ed-rule)] rounded-sm p-16 text-center">
          <p className="text-[var(--ed-ink-muted)]">No payments match this view.</p>
        </div>
      ) : (
        <div className="border border-[var(--ed-rule)] rounded-sm overflow-x-auto bg-[var(--ed-bg)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--ed-paper)] text-[var(--ed-ink-muted)] text-[0.6rem] uppercase tracking-[0.22em]">
              <tr>
                <th className="text-left px-5 py-3">Created</th>
                <th className="text-left px-5 py-3">Guest</th>
                <th className="text-left px-5 py-3">Property</th>
                <th className="text-left px-5 py-3">Amount</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Stripe</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`pay-row-${r.id}`} className="border-t border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:bg-[var(--ed-paper)]/60 transition-colors">
                  <td className="px-5 py-3.5 text-[var(--ed-ink-muted)] text-xs whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3.5">
                    <p className="text-[var(--ed-ink)]">{r.full_name}</p>
                    <p className="text-[var(--ed-ink-muted)] text-xs">{r.email}</p>
                  </td>
                  <td className="px-5 py-3.5 text-xs">{r.property_name || "—"}<div className="text-[var(--ed-ink-muted)] text-[0.65rem]">{r.check_in} → {r.check_out}</div></td>
                  <td className="px-5 py-3.5 text-[var(--ed-emerald)] font-medium whitespace-nowrap">{r.amount ? fmt(r.amount) : "—"}</td>
                  <td className="px-5 py-3.5"><StatusPill status={r.status} /></td>
                  <td className="px-5 py-3.5">
                    {r.stripe_session_id ? (
                      <a
                        href={`${stripeBase}/${r.stripe_session_id}`}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`pay-stripe-${r.id}`}
                        className="text-[0.6rem] uppercase tracking-[0.22em] border border-[var(--ed-rule)] hover:border-[var(--ed-emerald)] hover:text-[var(--ed-emerald)] text-[var(--ed-ink-soft)] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5"
                        title={r.stripe_session_id}
                      >
                        Open <ExternalLink size={10} />
                      </a>
                    ) : <span className="text-[var(--ed-ink-muted)] text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[var(--ed-ink-muted)] text-xs mt-5 leading-relaxed">
        Refunds and disputes are processed inside Stripe. Click <strong>Open</strong> next to a transaction to jump straight to the session in the Stripe Dashboard.
      </p>
    </div>
  );
}
